import { TooltipContentProps } from '@radix-ui/react-tooltip'
import { Avatar, type AvatarProps } from 'antd'
import _ from 'lodash'
import { ClipboardPenLineIcon, SparklesIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { InherentRiskCategoryEnum, Permission, Source } from '@/gen/inventory/v1/company_service_pb'

import { EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'

import {
  SOURCES_AVATAR_GROUP_LABELS,
  attackSurfacePhrase,
  dataAccessPhrase,
  defaultPermissionPhrase,
  morePermissionsLabel,
  permissionLabel,
} from './labels'

import LemaLogoIcon from '@/assets/lema-icon.svg'

type SourceIconConfig = {
  icon: React.ReactNode
  avatarClassName?: string
}

const SOURCE_ICONS: Record<string, SourceIconConfig> = {
  Projection: {
    icon: <SparklesIcon size={40} className='stroke-[1.5] text-main-foreground-ai' />,
    avatarClassName: 'border border-dashed !border-semantic-ai-700',
  },
  'Lema intake form': {
    icon: <ClipboardPenLineIcon size={14} className='text-main-foreground-secondary' />,
  },
  Lema: {
    icon: <LemaLogoIcon width={14} height={14} className='text-main-foreground-primary' />,
  },
}

type SourcesAvatarGroupProps = {
  sources: (Source | undefined)[]
  permissions?: Permission[]
  kind?: 'default' | 'inherent-risk'
  claim?: string
  explanations?: Record<string, string>
  trackingProps?: EventProperties
  categoryEnum?: InherentRiskCategoryEnum
  hideTooltip?: boolean
} & React.HTMLAttributes<HTMLDivElement>
export const SourcesAvatarGroup = ({
  sources,
  className,
  kind = 'default',
  permissions,
  claim,
  explanations,
  trackingProps,
  categoryEnum,
  hideTooltip,
}: SourcesAvatarGroupProps) => {
  const trackSuggestionHover = useTrackCallback('third-party.irq.suggestions.hover')

  if (sources.length === 0) {
    return null
  }

  const avatarGroupClassName = cn(
    'gap-0 transition-[gap,opacity] duration-200 hover:gap-2.5',
    className,
  )

  const avatarGroup = (
    <Avatar.Group
      className={avatarGroupClassName}
      max={{
        ...(sources.length > 4 ? { count: 3 } : {}),
        style: { width: 24, height: 24 },
      }}
    >
      {_.unionBy(sources, (source) => source && source.name)
        .sort((a, b) => {
          if (!a || !a.name) return 1
          if (!b || !b.name) return -1
          return a.name.localeCompare(b.name)
        })
        .map((source) => {
          return (
            <SourceAvatar
              key={`source-avatar-${source?.name}`}
              source={source}
              tooltip={hideTooltip ? undefined : source?.name}
            />
          )
        })}
    </Avatar.Group>
  )

  if (kind === 'default') {
    return avatarGroup
  }

  const hasAnyBody = sources.some(
    (source) =>
      source && getSourceBody({ source, permissions, claim, explanations, categoryEnum }) !== null,
  )

  if (!hasAnyBody) {
    return avatarGroup
  }

  return (
    <Tooltip
      trigger={avatarGroup}
      triggerClassName={avatarGroupClassName}
      onOpenChange={(open) => {
        if (open) {
          trackSuggestionHover({
            ...trackingProps,
            sources: sources.map((s) => s?.name),
            claim,
            permissions,
            explanation: explanations ? Object.values(explanations).join(' | ') : undefined,
          })
        }
      }}
    >
      <CombinedTooltip
        claim={claim}
        permissions={permissions}
        sources={sources || []}
        explanations={explanations}
        categoryEnum={categoryEnum}
      />
    </Tooltip>
  )
}

type SourceAvatarProps = {
  source?: Source
  tooltip?: string | React.ReactNode
  tooltipSide?: TooltipContentProps['side']
  /** antd Avatar size; a number sets an exact diameter in px. Defaults to the 24px group avatar. */
  size?: AvatarProps['size']
  /** Padding here insets the logo inside the circle, since the image fills the content box. */
  className?: string
}

export const SourceAvatar = ({
  source,
  tooltip,
  tooltipSide = 'bottom',
  size = 'small',
  className,
}: SourceAvatarProps) => {
  if (!source) {
    return null
  }

  const iconConfig = SOURCE_ICONS[source.name]
  if (!source.imgUrl && !iconConfig) {
    return null
  }

  const img = iconConfig
    ? iconConfig.icon
    : source.imgUrl && <img width={40} height={40} src={source.imgUrl} alt={source.name} />

  const avatar = (
    <Avatar
      className={cn(
        'rounded-full border !border-main-border-default bg-main-background-card',
        iconConfig?.avatarClassName,
        className,
      )}
      size={size}
    >
      {img}
    </Avatar>
  )

  if (tooltip) {
    return (
      <Tooltip key={`source-avatar-tooltip-${source.name}`} trigger={avatar} side={tooltipSide}>
        {tooltip}
      </Tooltip>
    )
  }

  return avatar
}

type CombinedTooltipProps = {
  permissions?: Permission[]
  sources: (Source | undefined)[]
  claim?: string
  explanations?: Record<string, string>
  categoryEnum?: InherentRiskCategoryEnum
}

type GetSourceBodyArgs = {
  source: Source
  permissions?: Permission[]
  claim?: string
  explanations?: Record<string, string>
  categoryEnum?: InherentRiskCategoryEnum
}

const getSourceBody = ({
  source,
  permissions,
  claim,
  explanations,
  categoryEnum,
}: GetSourceBodyArgs): React.ReactNode | null => {
  const uniquePermissions = permissions
    ?.filter((permission) => permission.source?.name === source.name)
    .reduce(
      (acc, permission) => {
        acc[permission.name] = permission
        return acc
      },
      {} as Record<string, Permission>,
    )

  const sourcePermissions = Object.values(uniquePermissions || {})
  const isIntake = source.name === 'Lema intake form'
  const hasPermissions = sourcePermissions.length > 0
  const isPermission =
    !isIntake &&
    hasPermissions &&
    (categoryEnum === InherentRiskCategoryEnum.DATA ||
      categoryEnum === InherentRiskCategoryEnum.ATTACK_SURFACE)

  const sourceExplanation = explanations?.[source.name]
  const hasExplanation = !!sourceExplanation && sourceExplanation.trim().length > 0

  if (isIntake) {
    return (
      <div className='text-main-foreground-secondary'>
        <span>{SOURCES_AVATAR_GROUP_LABELS.intakeRequestBasis}</span>
      </div>
    )
  }
  if (hasExplanation) {
    return (
      <div className='text-main-foreground-secondary'>
        <span>{sourceExplanation}</span>
      </div>
    )
  }
  if (isPermission) {
    return (
      <IntegrationPermissionTooltip
        source={source}
        permissions={sourcePermissions}
        claim={claim!}
        categoryEnum={categoryEnum}
      />
    )
  }
  return null
}

const CombinedTooltip = ({
  permissions,
  sources,
  claim,
  explanations,
  categoryEnum,
}: CombinedTooltipProps) => {
  const rows = sources.flatMap((source) => {
    if (!source) return []
    const body = getSourceBody({ source, permissions, claim, explanations, categoryEnum })
    if (!body) return []
    return [{ source, body }]
  })

  if (rows.length === 0) {
    return null
  }

  return (
    <div className='space-y-4 p-1'>
      {rows.map(({ source, body }) => (
        <div key={`combined-tooltip-${source.name}-${claim}`}>
          <SourceTitle source={source} />
          <div className='mt-1 space-x-1 text-main-foreground-secondary'>{body}</div>
        </div>
      ))}
    </div>
  )
}

const SourceTitle = ({ source }: { source: Source }) => {
  if (source.name === 'Projection') {
    return (
      <div className='flex items-center justify-start gap-2 text-main-foreground-ai'>
        <SourceAvatar source={source} />
        <span className='text-md font-semibold'>
          {SOURCES_AVATAR_GROUP_LABELS.lemaSmartEstimation}
        </span>
      </div>
    )
  }

  return (
    <div className='flex items-center justify-start gap-2'>
      <SourceAvatar source={source} />
      <span className='text-md font-semibold'>{source.name}</span>
    </div>
  )
}

type IntegrationPermissionTooltipProps = {
  permissions: Permission[]
  claim: string
  source: Source
  categoryEnum?: InherentRiskCategoryEnum
}
const IntegrationPermissionTooltip = ({
  permissions,
  claim,
  source,
  categoryEnum,
}: IntegrationPermissionTooltipProps) => {
  const phrase = useMemo(() => {
    const isPlural = permissions.length > 1

    switch (categoryEnum) {
      case InherentRiskCategoryEnum.ATTACK_SURFACE:
        return attackSurfacePhrase(isPlural, claim)
      case InherentRiskCategoryEnum.DATA:
        return dataAccessPhrase(isPlural, claim)
      default:
        return defaultPermissionPhrase(claim)
    }
  }, [categoryEnum, claim, permissions.length])

  return (
    <span
      key={`integration-permission-${source.name}-${claim}-${permissions.length}`}
      className='leading-5'
    >
      <span className='mr-1'>{permissionLabel(permissions.length)}</span>
      <PermissionTooltip permissions={permissions} shown={3} />
      <span className='ml-1'>{phrase}</span>
    </span>
  )
}

const PermissionTooltip = ({
  permissions,
  shown = 3,
}: {
  permissions: Permission[]
  shown?: number
}) => {
  const shownPermissions = permissions.slice(0, shown)
  const hiddenPermissions = permissions.slice(shown)

  return (
    <>
      {shownPermissions?.map((permission, index) => {
        const permissionName = permission.name.replace(/^.*\.com\/auth/, '').replace('/', '')
        return (
          <PermissionBadge
            key={`permission-${permissionName}-${index}`}
            permissionName={permissionName}
            isLast={index === permissions.length - 1}
          />
        )
      })}
      {hiddenPermissions.length > 0 && (
        <PermissionBadge
          permissionName={morePermissionsLabel(hiddenPermissions.length)}
          isLast={true}
          dir='ltr'
        />
      )}
    </>
  )
}

const PermissionBadge = ({
  permissionName,
  isLast,
  dir = 'rtl',
}: {
  permissionName: string
  isLast: boolean
  dir?: 'rtl' | 'ltr'
}) => {
  return (
    <React.Fragment key={`permission-${permissionName}`}>
      <span
        className='inline-block max-w-40 overflow-hidden text-ellipsis text-nowrap rounded border-y border-main-border-default bg-main-background-muted px-0.5 align-text-top font-mono text-xs'
        dir={dir}
      >
        {permissionName}
      </span>
      {!isLast && ', '}
    </React.Fragment>
  )
}
